import {
  CheckCircle,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
  Publish,
  ReportProblem,
  Restore,
} from '@mui/icons-material';
import {
  Box, Button, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import * as React from 'react';
import {
  useCallback, useEffect, useMemo, useReducer,
} from 'react';
import { compareArrayOfObjects } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { getBuildingPath } from 'shared/routes/building';
import { moveVlanBuildingRows } from 'shared/api/complexes';
import { useRequest } from 'shared/hooks';

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'set':
      return { ...state, ...payload };
    default:
      return state;
  }
}

/**
 * Supports reordering buildings.
 * @param {number} complexId - ID of the complex
 * @param {number} minVlan - Minimum VLAN value
 * @param {number} maxVlan - Maximum VLAN value
 * @param {Function} onSave - Callback function when VLAN changes are saved
 * @param {Array} orderedBuildings - Array of buildings with VLAN assignments
 * @param {number} vlanMargin - VLAN margin for redistribution
 */
export default function VlanEditDataGrid({
  complexId, minVlan, maxVlan, onSave, orderedBuildings, vlanMargin,
}) {
  const { request } = useRequest();
  const [state, dispatch] = useReducer(reducer, {
    rows: orderedBuildings,
    dirty: false,
    disabled: false,
  });
  const { rows, dirty, disabled } = state;

  const theme = useTheme();
  const compactedColumns = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let val = false;
    if (!compareArrayOfObjects(rows, orderedBuildings)) {
      val = true;
    }
    dispatch({ action: 'set', payload: { dirty: val } });
  }, [orderedBuildings, rows]);

  useEffect(() => {
    const missingDot1x = orderedBuildings.find((row) => row.dot1x === null);
    dispatch({ action: 'set', payload: { disabled: !!missingDot1x } });
  }, [orderedBuildings]);

  const handleRevert = useCallback(() => {
    dispatch({ action: 'set', payload: { rows: orderedBuildings, dirty: false } });
  }, [orderedBuildings]);

  const handleMoveAction = useCallback(async (currentIndex, newIndex) => {
    const results = await request(moveVlanBuildingRows(complexId, {
      buildings: rows,
      current_index: currentIndex,
      new_index: newIndex,
      vlan_margin: vlanMargin,
      min_vlan: minVlan,
      max_vlan: maxVlan,
    }));
    if (results) {
      const newRows = results.map((building, index) => ({
        ...building,
        index,
      }));
      dispatch({ action: 'set', payload: { rows: newRows } });
    }
  }, [complexId, maxVlan, minVlan, request, rows, vlanMargin]);

  const cellLink = (params) => (
    <a href={getBuildingPath(params.row.id)}>
      {params.row.name}
    </a>
  );

  const dot1xCheck = (params) => (
    params.row?.dot1x ? <CheckCircle color="success" /> : (
      <Tooltip title={`Redistribution Disabled: ${params.row.name} Missing Dot1x Profile`}>
        <ReportProblem color="warning" />
      </Tooltip>
    )
  );

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Building',
      width: compactedColumns ? 150 : 350,
      renderCell: cellLink,
    },
    {
      field: 'unit_count',
      headerName: 'Unit Count',
      align: 'left',
      renderCell: ({ row }) => row?.units.length,
    },
    {
      field: 'vlan_range_start',
      headerName: 'VLAN Start',
      type: 'string',
      align: 'left',
    },
    {
      field: 'vlan_range_end',
      headerName: 'VLAN End',
      type: 'string',
      align: 'left',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 200,
      editable: true,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<KeyboardArrowUp />}
          label="Move Building Up"
          onClick={() => handleMoveAction(row.index, row.index - 1)}
          color="inherit"
          disabled={row.index === 0}
        />,
        <GridActionsCellItem
          icon={<KeyboardDoubleArrowUp />}
          label="Move Building to Top"
          onClick={() => handleMoveAction(row.index, 0)}
          color="inherit"
          disabled={row.index === 0}
        />,
        <GridActionsCellItem
          icon={<KeyboardDoubleArrowDown />}
          label="Move Building to Bottom"
          onClick={() => handleMoveAction(row.index, rows.length - 1)}
          color="inherit"
          disabled={row.index === rows.length - 1}
        />,
        <GridActionsCellItem
          icon={<KeyboardArrowDown />}
          label="Move Building Down"
          onClick={() => handleMoveAction(row.index, row.index + 1)}
          color="inherit"
          disabled={row.index === rows.length - 1}
        />,
      ],
    },
    {
      field: 'Dot1x',
      align: 'left',
      renderCell: dot1xCheck,
    },
  ], [compactedColumns, handleMoveAction, rows.length]);

  return (
    <Box>
      <Box sx={{
        width: '100%',
      }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            mb: 2,
          }}
        >
          <Typography color="text.secondary">
            {`Total Results: ${rows?.length}`}
          </Typography>
        </Box>
        {rows?.length === 0 ? (
          <Typography>This complex has no buildings</Typography>
        ) : (
          <DataGrid
            sx={{ width: '100%', margin: 0 }}
            rows={rows}
            columns={columns}
            experimentalFeatures={{ clipboardPaste: true }}
            hideFooter
          />
        )}
      </Box>
      <Box
        style={{ marginBottom: 8 }}
        sx={{
          display: 'flex',
          justifyContent: 'end',
          gap: 1,
          py: 2,
        }}
      >
        {dirty && (
        <Button
          onClick={() => handleRevert()}
          startIcon={<Restore />}
          variant={VARIANTS.outlined}
          color={BUTTON_COLORS.error}
        >
          Reset
        </Button>
        )}
        <Button
          disabled={disabled}
          onClick={() => onSave(rows)}
          startIcon={<Publish />}
          variant={VARIANTS.contained}
        >
          Apply Redistribution
        </Button>
      </Box>
    </Box>
  );
}
